import { useState } from 'react';
import { IconCollapse, IconExpand, PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import { RiskScoringTypes, CustomerType } from '../../../hoc/customers/types';
import TotalRiskScoring from '../../partial/aml/total-risk-scoring';
import ExpandableRow from '../../partial/aml/expandable-row';
import ExpandableRowContent from '../../partial/aml/expandable-row-content';
import TypeOfClient from '../../partial/aml/type-of-client';
import DeliveryChannels from '../../partial/aml/delivery-channels';
import Products from '../../partial/aml/products';
import SourceOfFunds from '../../partial/aml/source-of-funds';
import { AMLRiskScoringTypes } from '../types';

import cn from './aml.module.css';

const RiskScoring = ({
  aml,
  onUpdate,
  isEditable,
  updateInProgress,
  updateIsSuccess,
  amlUpdateStatus,
  calculateAllowed,
  status,
  isLoading,
  users,
  manualScoreAllowed,
  setRiskScoreChangeLogsSidepanelVisible,
  setManualScoreSidepanelVisible,
}: AMLRiskScoringTypes): React.JSX.Element => {
  const [expandedRows, setExpandedRows] = useState<string[]>([
    RiskScoringTypes.DELIVERY_CHANNEL,
    RiskScoringTypes.GEOGRAPHICAL_RISK,
    RiskScoringTypes.PRODUCTS,
    RiskScoringTypes.TYPE_OF_CLIENT,
    RiskScoringTypes.SOURCE_OF_FUNDS,
    RiskScoringTypes.MONTHLY_VOLUMES,
  ]);
  const [blockCollapsed, setBlockCollapsed] = useState(false);

  const { countries, monthlyTurnover, activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const onExpandClick = () => {
    if (expandedRows.length) {
      setExpandedRows([]);
    } else {
      setExpandedRows([
        RiskScoringTypes.DELIVERY_CHANNEL,
        RiskScoringTypes.GEOGRAPHICAL_RISK,
        RiskScoringTypes.PRODUCTS,
        RiskScoringTypes.TYPE_OF_CLIENT,
        RiskScoringTypes.SOURCE_OF_FUNDS,
        RiskScoringTypes.MONTHLY_VOLUMES,
      ]);
    }
  };

  return (
    <div className={cn.riskScoringContent}>
      <TotalRiskScoring
        aml={aml}
        // logs={aml?.totalRisk.totalRiskScoreLogs}
        updateIsSuccess={updateIsSuccess}
        updateInProgress={updateInProgress}
        onUpdate={onUpdate}
        onCollapse={() => setBlockCollapsed(!blockCollapsed)}
        blockCollapsed={blockCollapsed}
        amlUpdateStatus={amlUpdateStatus}
        calculateAllowed={calculateAllowed}
        status={status}
        isLoading={isLoading}
        users={users}
        manualScoreAllowed={manualScoreAllowed}
        setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
        changeLogsAvailable={activePermissions?.includes(
          PermissionEnum.INDIVIDUAL_CUSTOMERS_SCORE_LIST,
        )}
        setRiskScoreChangeLogsSidepanelVisible={
          setRiskScoreChangeLogsSidepanelVisible
        }
      />
      {blockCollapsed && (
        <>
          <div className={cn.collapseButtonWrapper}>
            <button
              type="button"
              className={cn.collapseAllButton}
              onClick={onExpandClick}
            >
              {!expandedRows?.length ? 'Expand all' : 'Collapse all'}
              {!expandedRows?.length ? (
                <IconExpand width={15} height={15} className={cn.iconExpand} />
              ) : (
                <IconCollapse width={15} className={cn.iconCollapse} />
              )}
            </button>
          </div>
          <ExpandableRow
            name={RiskScoringTypes.GEOGRAPHICAL_RISK}
            title="Geographical risk"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.geographicalRiskTotal as number}
            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            <ExpandableRowContent
              title="Residency:"
              value={aml?.countryId as string}
              risk={
                aml?.countryRisk?.toString() !== 'null' &&
                aml?.countryRisk?.toString() !== 'undefined'
                  ? (aml?.countryRisk?.toString() as string)
                  : '-'
              }
              list={countries}
              name="residency"
              isEditable={isEditable}
            />
            <ExpandableRowContent
              title="Citizenship:"
              value={aml?.nationalityId as string}
              risk={
                aml?.nationalityRisk?.toString() !== 'null' &&
                aml?.nationalityRisk?.toString() !== 'undefined'
                  ? (aml?.nationalityRisk?.toString() as string)
                  : '-'
              }
              list={countries}
              name="citizenship"
              isEditable={isEditable}
            />
          </ExpandableRow>
          <TypeOfClient
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
          />
          <DeliveryChannels
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={CustomerType.INDIVIDUAL}
          />
          <Products
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={CustomerType.INDIVIDUAL}
          />
          <SourceOfFunds
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
          />
          <ExpandableRow
            name={RiskScoringTypes.MONTHLY_VOLUMES}
            title="Turnover"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.monthlyTurnoverRangeRisk as number}
            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            <ExpandableRowContent
              title="Turnover:"
              value={aml?.monthlyTurnoverRangeId as string}
              risk={
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'null' &&
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'undefined'
                  ? (aml?.monthlyTurnoverRangeRisk?.toString() as string)
                  : '-'
              }
              list={monthlyTurnover}
              name="monthlyTurnoverRangeId"
              isEditable={isEditable}
            />
          </ExpandableRow>
        </>
      )}
    </div>
  );
};

export default RiskScoring;
